import React, { useState, BaseSyntheticEvent, useCallback, useMemo } from 'react'
import { IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonList, IonRow, IonText, IonToggle } from '@ionic/react'
import { chevronDown, chevronUp } from 'ionicons/icons'
import LabelComponent from '../label/Label.component'
import './SelectableDataGrid.component.css'
import { convertISOToHourAndMinute } from '../../utilities/ekgImport.util'
import { useWindowSize } from '../../hooks/useWindowSize'

const formatTemperature = (row: Record<string, unknown>): string => {
  const temp = row.temperature
  if (!temp) return ''
  const tempType = row.temperatureType || 'C'
  return `${temp} ${tempType}°`
}

const formatTime = (row: Record<string, unknown>): string => {
  return row.dateTime ? convertISOToHourAndMinute(row.dateTime as string) : ''
}

const VITALS_COLUMNS = [
  { key: 'time', label: 'Time', formatter: formatTime },
  { key: 'comments', label: 'Description', isDescription: true },
  { key: 'heartRate', label: 'HR' },
  { key: 'systolicBP', label: 'SBP' },
  { key: 'diastolicBP', label: 'DBP' },
  { key: 'spo2', label: 'SpO2' },
  { key: 'etCO2', label: 'EtCo2' },
  { key: 'respirationRate', label: 'Resp' },
  { key: 'glucose', label: 'Glucose' },
  { key: 'temperature', label: 'Temp', formatter: formatTemperature },
]

export interface IDataGridColumn {
  key: string
  label: string
  isDescription?: boolean
  formatter?: (row: Record<string, unknown>) => string
}

export interface ISelectableDataGridProps {
  columns?: IDataGridColumn[]
  data: Record<string, any>[]
  showToggle?: boolean
  selectedItems: any
  onToggleAll: (checked: boolean) => void
  onToggleItem: (checked: boolean, item: Record<string, any>) => void
}

const SelectableDataGrid: React.FC<ISelectableDataGridProps> = ({
  columns = VITALS_COLUMNS,
  data,
  showToggle = true,
  selectedItems,
  onToggleAll,
  onToggleItem,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const { width: windowWidth } = useWindowSize()
  const isAllSelected = useMemo(() => selectedItems?.length === data?.length || false, [selectedItems, data])

  const handleToggleItem = (e: BaseSyntheticEvent, item: Record<string, any>) => {
    e.preventDefault()
    const checked = e.target.checked
    onToggleItem(checked, item)
  }

  const handleToggleAll = (e: BaseSyntheticEvent) => {
    const checked = e.target.checked
    onToggleAll(checked)
  }

  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedItems(newExpanded)
  }

  const getVisibleColumnCount = (): number => {
    if (windowWidth >= 1280) return 13
    if (windowWidth >= 1180) return 12
    if (windowWidth >= 1024) return 10
    if (windowWidth >= 900) return 9
    if (windowWidth >= 800) return 7
    if (windowWidth >= 640) return 6
    if (windowWidth >= 620) return 5
    return 4
  }

  const visibleColumnCount = getVisibleColumnCount()
  const hiddenColumns = columns.slice(visibleColumnCount)

  const allowExpand = (row: Record<string, any>): boolean => {
    return hiddenColumns.some((column) => {
      const value = row[column.key]
      return value != null && value !== ''
    })
  }

  const renderTag = (column: IDataGridColumn, value: string) => {
    return (
      <IonItem key={column.key} className='selectable-data-grid-tag'>
        <IonRow className='ion-no-padding w-100'>
          <IonCol size='12' className='ion-no-padding'>
            <IonLabel className='selectable-data-grid-tag-label'>{column.label}</IonLabel>
            <IonText className='selectable-data-grid-tag-value'>{value}</IonText>
          </IonCol>
        </IonRow>
      </IonItem>
    )
  }

  const renderHiddenColumns = (row: Record<string, any>) => {
    return (
      <>
        {hiddenColumns.map((column) => {
          let displayValue = ''
          if (column.formatter) {
            displayValue = column.formatter(row)
          } else {
            const value = row[column.key]
            displayValue = value != null && value !== '' ? String(value) : ''
          }
          if (!displayValue) return null
          return <React.Fragment key={column.key}>{renderTag(column, displayValue)}</React.Fragment>
        })}
      </>
    )
  }

  const gridClassName = showToggle ? 'selectable-data-grid-grid' : 'selectable-data-grid-grid-summary'

  const isSelected = useCallback(
    (id: string): boolean => {
      return selectedItems?.find((item: any) => item.key?.toString() === id) !== undefined || false
    },
    [selectedItems]
  )

  return (
    <IonList lines='full' className='selectable-data-grid-list'>
      <IonItem className='selectable-data-grid-header-item selectable-data-grid-main'>
        {showToggle && <IonToggle slot='start' checked={isAllSelected} onClick={handleToggleAll} />}
        <IonGrid className={gridClassName}>
          <IonRow className='selectable-data-grid-header-row'>
            {columns.map((column) => (
              <IonCol
                key={column.key}
                className={`selectable-data-grid-column selectable-data-grid-column-${column.key} ${
                  column.isDescription ? 'selectable-data-grid-description' : ''
                }`}>
                <IonLabel>
                  <h3>{column.label}</h3>
                </IonLabel>
              </IonCol>
            ))}
            <IonCol className='selectable-data-grid-more'></IonCol>
          </IonRow>
        </IonGrid>
      </IonItem>
      <hr className='first-divider' />
      {data.map((row, index) => {
        const isExpanded = expandedItems.has(index)
        const canExpand = allowExpand(row)
        return (
          <>
            <IonItem key={index} className='selectable-data-grid-main'>
              {showToggle && (
                <IonToggle
                  slot='start'
                  checked={isSelected(row.key)}
                  onClick={(e: BaseSyntheticEvent) => handleToggleItem(e, row)}
                />
              )}
              <IonGrid className={gridClassName}>
                <IonRow className='selectable-data-grid-row'>
                  {columns.map((column) => {
                    let displayValue = ''
                    if (column.formatter) {
                      displayValue = column.formatter(row)
                    } else {
                      const value = row[column.key]
                      displayValue = value != null && value !== '' ? String(value) : ''
                    }
                    return (
                      <IonCol
                        key={column.key}
                        className={`selectable-data-grid-column selectable-data-grid-column-${
                          column.key
                        } selectable-data-grid-data-point ${
                          column.isDescription ? 'selectable-data-grid-description' : ''
                        }`}>
                        <LabelComponent isPHI={false} label={displayValue} size={3} />
                      </IonCol>
                    )
                  })}

                  <IonCol className='selectable-data-grid-more' onClick={() => canExpand && toggleExpand(index)}>
                    {canExpand && (
                      <IonIcon
                        color='medium'
                        className='selectable-data-grid-more-icon'
                        icon={isExpanded ? chevronUp : chevronDown}
                      />
                    )}
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
            {canExpand && isExpanded && <div className='selectable-data-grid-tags'>{renderHiddenColumns(row)}</div>}
          </>
        )
      })}
    </IonList>
  )
}

export default SelectableDataGrid
